import { useEffect, useMemo } from "react";

type Props = {
  follow: boolean;
  onCancel: () => void;
  scrollContainerRef: React.RefObject<HTMLElement | Document>;
  contentRef: React.RefObject<HTMLDivElement>;
};

/**
 * useScrollFollower automatically scrolls a container to the bottom when the inner content changes size.
 *
 * If a manual scroll is detected, the automatic scrolling is canceled.
 */
export const useScrollFollow = ({ follow, onCancel, contentRef, scrollContainerRef }: Props) => {
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (follow) {
      el?.addEventListener("scroll", onCancel);
    }

    return () => {
      el?.removeEventListener("scroll", onCancel);
    };
  }, [follow]);

  const resizeObserver = useMemo(() => {
    return new ResizeObserver(() => {
      requestAnimationFrame(() => {
        scrollContainerRef.current?.removeEventListener("scroll", onCancel);
        contentRef.current?.scrollIntoView({ block: "end" });

        // Short pause to wait for the scroll event to finish before re-enabling automatic scrolling
        setTimeout(() => {
          scrollContainerRef.current?.addEventListener("scroll", onCancel);
        }, 100);
      });
    });
  }, []);

  useEffect(() => {
    if (!contentRef.current) {
      return;
    }

    if (follow) {
      resizeObserver.observe(contentRef.current);
    } else {
      resizeObserver.disconnect();
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [follow, resizeObserver]);
};
